import { Accordion, Button, Card, Collapse, NumberInput, Select, Stack, Switch, TagsInput, TextInput, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { JoinOptions, Layer } from "../NewTileset";
import {
  cleanOptions,
  tileJoinFields,
  tileJoinGroups,
  type FieldDescriptor,
  type TileJoinGroup,
} from "../constants/tippecanoeFields";
import { buildTileJoinCommand } from "../utils/buildCommand";
import { CommandPreview } from "./CommandPreview";

export interface TilesetJoinOptionsProps {
  isLoading: boolean;
  getInputProps: (name: string, opts?: { type?: "checkbox" | "input" }) => object;
  layers: Layer[];
  displayName: string;
  joinOptions: JoinOptions;
}

interface FieldRendererProps {
  field: FieldDescriptor<TileJoinGroup>;
  isLoading: boolean;
  getInputProps: TilesetJoinOptionsProps["getInputProps"];
}

function FieldRenderer({ field, isLoading, getInputProps }: FieldRendererProps) {
  const path = `joinOptions.${field.key}`;

  switch (field.kind) {
    case "boolean":
      return (
        <Switch
          label={field.key}
          description={field.description}
          disabled={isLoading}
          {...getInputProps(path, { type: "checkbox" })}
        />
      );
    case "number":
      return (
        <NumberInput
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          disabled={isLoading}
          min={field.min}
          max={field.max}
          {...getInputProps(path)}
        />
      );
    case "stringArray":
      return (
        <TagsInput
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          disabled={isLoading}
          clearable
          {...getInputProps(path)}
        />
      );
    case "enum":
      return (
        <Select
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          data={field.options ? [...field.options] : []}
          disabled={isLoading}
          clearable
          {...getInputProps(path)}
        />
      );
    case "string":
    default:
      return (
        <TextInput
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          disabled={isLoading}
          {...getInputProps(path)}
        />
      );
  }
}

export const TilesetJoinOptions: FC<TilesetJoinOptionsProps> = ({
  isLoading,
  getInputProps,
  layers,
  displayName,
  joinOptions,
}) => {
  const intl = useIntl();
  const [opened, { toggle }] = useDisclosure(false);
  const fields = useMemo(() => tileJoinFields(intl), [intl]);
  const groups = useMemo(() => tileJoinGroups(intl), [intl]);

  const commonFields = fields.filter(f => f.group === "common");
  const advancedGroups = groups.filter(
    g => fields.some(f => f.group === g.group),
  );

  const description = intl.formatMessage({
    defaultMessage: "Options applied when joining multiple layers into the final tileset.",
    id: "label.tilesetJoinOptionsDescription",
  });

  const joinCommand = useMemo(() => {
    const tilesetName = displayName || "<tileset>";
    const inputs = layers.map(l => `${l.name || "<layer>"}.mbtiles`);
    const output = `${tilesetName}.mbtiles`;
    const cleaned = cleanOptions(joinOptions);
    const opts = { ...cleaned, name: cleaned.name ?? tilesetName };

    return [buildTileJoinCommand(inputs, output, opts)];
  }, [layers, displayName, joinOptions]);

  return (
    <Stack gap="xs">
      <Title order={3}>
        <FormattedMessage defaultMessage="Join options" id="label.tilesetJoinOptions" />
      </Title>
      {description}

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="xs">
          {commonFields.map(field => (
            <FieldRenderer
              key={field.key}
              field={field}
              isLoading={isLoading}
              getInputProps={getInputProps}
            />
          ))}

          <Button
            variant="subtle"
            color="gray"
            onClick={toggle}
            leftSection={opened ? <IconChevronDown size="1rem" /> : <IconChevronRight size="1rem" />}
            justify="flex-start"
            fullWidth
          >
            <FormattedMessage id="button.advancedOptions" defaultMessage="Advanced options" />
          </Button>

          <Collapse expanded={opened}>
            <Accordion variant="separated" chevronPosition="right" multiple>
              {advancedGroups.map((group) => {
                const groupFields = fields.filter(f => f.group === group.group);
                if (groupFields.length === 0) return null;

                return (
                  <Accordion.Item key={group.group} value={group.group}>
                    <Accordion.Control>
                      <Text fw={500}>{group.label}</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        {groupFields.map(field => (
                          <FieldRenderer
                            key={field.key}
                            field={field}
                            isLoading={isLoading}
                            getInputProps={getInputProps}
                          />
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Collapse>

          <CommandPreview commands={joinCommand} />
        </Stack>
      </Card>
    </Stack>
  );
};
